/* =============================================================================
   RYNOR — menus (v17)
   1) Phone / iPad menu: the ☰ in the header opens <aside id="rynor-mnav"> right
      under the header (the header stays visible), the ☰ turns into ✕, and the
      page behind stops scrolling. ✕, Esc, rotating to desktop, or opening
      search / the bag closes it.
   2) Rows with an arrow open and close smoothly, one at a time.
   3) Theme editor: selecting a mega-menu block shows that mega menu; selecting
      the "Mobile navigation" section opens the phone menu.
   Styles: assets/rynor-mobile-menu.css + assets/rynor-mega-menu.css
   v38: the open drawer follows the header if the header moves after ☰ is tapped.
   ============================================================================= */
(function () {
  'use strict';
  if (window.RynorMenu) return;

  var root = document.documentElement;
  var OPEN = 'rynor-mnav-open';
  var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var lastToggle = null;

  function all(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }
  function shown(el) { return !!(el && el.getClientRects().length); }
  function drawer() { return document.getElementById('rynor-mnav'); }
  function isOpen() { return root.classList.contains(OPEN); }
  function reducedMotion() { return !!(motionQuery && motionQuery.matches); }
  function inEditor() { return !!(window.Shopify && window.Shopify.designMode); }

  /* ---------- drawer ---------- */
  function headerBar() {
    var bars = all('header.section-header .section-header__mobile, header.section-header .section-header__main-bar');
    for (var i = 0; i < bars.length; i++) { if (shown(bars[i])) return bars[i]; }
    return null;
  }

  // The drawer starts exactly where the visible header ends.
  var placedTop = -1;
  function place() {
    var bar = headerBar();
    var bottom = bar ? bar.getBoundingClientRect().bottom : 0;
    var top = Math.max(0, Math.round(bottom));
    if (top === placedTop) return false;
    placedTop = top;
    root.style.setProperty('--rynor-mnav-top', top + 'px');
    return true;
  }

  // v38: the drawer used to be measured once, when ☰ was tapped. If the header moved
  // after that (the page jumping to the top - fixed in rynor-mobile-menu.css - or still
  // gliding from a scroll, or the browser bars sliding in), the menu covered part of the
  // header. While the menu is open the drawer now follows the header: it re-measures every
  // frame until the header has held still for a moment, and any scroll / resize restarts it.
  var followFrame = 0;
  var followUntil = 0;
  var stillFrames = 0;
  function followHeader(ms) {
    followUntil = Math.max(followUntil, Date.now() + (ms || 0));
    stillFrames = 0;
    if (followFrame) return;
    followFrame = requestAnimationFrame(function tick() {
      followFrame = 0;
      if (!isOpen()) return;
      stillFrames = place() ? 0 : stillFrames + 1;
      if (Date.now() < followUntil || stillFrames < 20) followFrame = requestAnimationFrame(tick);
    });
  }

  function syncToggles(open) {
    all('.js-rynor-mnav-toggle').forEach(function (btn) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      var label = btn.getAttribute(open ? 'data-label-close' : 'data-label-open');
      if (label) btn.setAttribute('aria-label', label);
    });
  }

  function focusables(scope) {
    return all('a[href], button:not([disabled]), summary, input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])', scope).filter(shown);
  }

  function open(options) {
    options = options || {};
    var el = drawer();
    if (!el) return;
    if (options.toggle) lastToggle = options.toggle;
    place();
    el.setAttribute('aria-hidden', 'false');
    root.classList.add(OPEN);
    followHeader(1200);
    syncToggles(true);
    if (options.focus) {
      var first = focusables(el)[0];
      if (first) first.focus({ preventScroll: true });
    }
  }

  function close(options) {
    options = options || {};
    if (!isOpen()) return;
    var el = drawer();
    root.classList.remove(OPEN);
    if (el) el.setAttribute('aria-hidden', 'true');
    syncToggles(false);
    if (options.restoreFocus && shown(lastToggle)) lastToggle.focus({ preventScroll: true });
  }

  // Capture phase: some header buttons (search) stop their clicks from bubbling.
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var toggle = target.closest('.js-rynor-mnav-toggle');
    if (toggle) {
      event.preventDefault();
      if (isOpen()) close();
      else open({ toggle: toggle, focus: event.detail === 0 }); // detail 0 = keyboard
      return;
    }
    if (!isOpen()) return;

    // Opening search, the bag or the country popup closes the menu first.
    if (target.closest('.js-search-toggle, .js-slideout-open, .js-mini-cart-trigger, [data-ry-locale-open]')) {
      close();
      return;
    }
    // A link to a spot on this same page: close so the page shows.
    if (target.closest('#rynor-mnav a[href^="#"]')) close();
  }, true);

  document.addEventListener('keydown', function (event) {
    if (!isOpen()) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      close({ restoreFocus: true });
      return;
    }
    if (event.key !== 'Tab') return;
    // Keep keyboard focus inside the ✕ button + the menu while it is open.
    var el = drawer();
    if (!el) return;
    var toggle = shown(lastToggle) ? lastToggle : all('.js-rynor-mnav-toggle').filter(shown)[0];
    var ring = (toggle ? [toggle] : []).concat(focusables(el));
    if (!ring.length) return;
    var index = ring.indexOf(document.activeElement);
    var next = index === -1
      ? (event.shiftKey ? ring.length - 1 : 0)
      : (index + (event.shiftKey ? -1 : 1) + ring.length) % ring.length;
    event.preventDefault();
    ring[next].focus();
  });

  var resizeFrame = 0;
  window.addEventListener('resize', function () {
    if (!isOpen()) return;
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(function () {
      var toggleVisible = all('.js-rynor-mnav-toggle').some(shown);
      if (!toggleVisible && !inEditor()) close();
      else followHeader(300);
    });
  });

  // The page can keep gliding after the tap (iPhone momentum scroll), and the browser
  // bars can slide in / out: keep the drawer on the header while that happens.
  window.addEventListener('scroll', function () { if (isOpen()) followHeader(300); }, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', function () { if (isOpen()) followHeader(300); });
  }

  // Leaving the page with the menu open (then pressing Back) shouldn't bring it back open.
  window.addEventListener('pagehide', function () { close(); });

  /* ---------- rows with an arrow (details / summary) ---------- */
  function animateHeight(panel, from, to, done) {
    if (panel._rynorAnimation) panel._rynorAnimation.cancel();
    if (reducedMotion() || !panel.animate) { done(); return; }
    var animation = panel.animate(
      [{ height: from + 'px' }, { height: to + 'px' }],
      { duration: 300, easing: 'cubic-bezier(.4, 0, .2, 1)' }
    );
    panel._rynorAnimation = animation;
    animation.onfinish = function () { panel._rynorAnimation = null; done(); };
    animation.oncancel = function () { panel._rynorAnimation = null; };
  }

  function collapse(details) {
    var panel = details.querySelector('.rynor-mnav__sub');
    if (!details.open) return;
    details.classList.add('is-closing');
    var finish = function () { details.open = false; details.classList.remove('is-closing'); };
    if (!panel) { finish(); return; }
    animateHeight(panel, panel.getBoundingClientRect().height, 0, finish);
  }

  function expand(details) {
    var scope = details.closest('.rynor-mnav') || document;
    all('details.rynor-mnav__acc[open]', scope).forEach(function (other) {
      if (other !== details) collapse(other);
    });
    var panel = details.querySelector('.rynor-mnav__sub');
    var start = details.open && panel ? panel.getBoundingClientRect().height : 0;
    details.classList.remove('is-closing');
    details.open = true;
    if (!panel) return;
    animateHeight(panel, start, panel.scrollHeight, function () {});
  }

  document.addEventListener('click', function (event) {
    var summary = event.target && event.target.closest && event.target.closest('.rynor-mnav summary.rynor-mnav__row');
    if (!summary) return;
    var details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return;
    event.preventDefault();
    if (details.open && !details.classList.contains('is-closing')) collapse(details);
    else expand(details);
  });

  /* ---------- theme editor ---------- */
  function isMobileNavEvent(event) {
    var el = event.target;
    return !!(el && el.querySelector && el.querySelector('[data-section-type="mobile-navigation"]'));
  }
  document.addEventListener('shopify:section:select', function (event) {
    if (isMobileNavEvent(event)) open();
  });
  document.addEventListener('shopify:section:deselect', function (event) {
    if (isMobileNavEvent(event)) close();
  });
  document.addEventListener('shopify:section:load', function () {
    syncToggles(isOpen());
    if (isOpen()) place();
  });
  document.addEventListener('shopify:block:select', function (event) {
    var item = event.target && event.target.closest && event.target.closest('li.dropdown--meganav');
    if (!item) return;
    all('li.dropdown--meganav.rynor-mega-pinned').forEach(function (other) { other.classList.remove('rynor-mega-pinned'); });
    item.classList.add('rynor-mega-pinned');
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    var item = event.target && event.target.closest && event.target.closest('li.dropdown--meganav');
    if (item) item.classList.remove('rynor-mega-pinned');
  });

  window.RynorMenu = { open: open, close: close, isOpen: isOpen };
})();
